import { Modal, Tag, Typography } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Answers,
	Feedback,
	PredefinedReason,
} from '../../shared/VolunteerApplicationServiceTypes';
import { ReviewAction } from '../../shared/Constants';
import GreenRightArrow from '../../assets/images/green-right-arrow.svg';
import OrangeRightArrow from '../../assets/images/orange-right-arrow.svg';
import BlueRightArrow from '../../assets/images/blue-right-arrow.svg';
import CloseIcon from '../../assets/images/close-icon.svg';
import Icon from '../../components/ui-components/Icon';
import { CategoryStepUIConfig } from '../../shared/CategoryStepUIConfig';
import { VIEW_QUESTIONS_ANSWERS } from '../../shared/Routes';
import { useBatchId } from '../../hooks/useBatchId';

const { Text } = Typography;

const ResultTag = ({
	color,
	reviewer,
	comments,
	predefinedReasons = [],
	answers,
	uiConfig,
	name,
	result,
	hideGoToQuestions = false,
}: {
	color: string;
	reviewer: string;
	predefinedReasons?: Array<PredefinedReason>;
	comments?: string;
	answers?: Answers;
	uiConfig?: CategoryStepUIConfig;
	name: string;
	result?: Feedback;
	hideGoToQuestions?: boolean;
}) => {
	const { t, i18n } = useTranslation();
	const isLengthyLanguage =
		i18n.language === 'ta' || i18n.language === 'ma' || i18n.language === 'te';
	const { navigateWithBatchId } = useBatchId();
	const [isModalVisible, setIsModalVisible] = useState(false);
	const skipModal =
		(reviewer === 'preceptor' || reviewer === 'family') && !hideGoToQuestions;
	const commentsLabel =
		{
			application: 'candidatesOverallComments',
			preceptor: 'preceptorRecommendation',
			otherReference: 'otherReferenceRecommendation',
		}[reviewer as 'application' | 'preceptor' | 'otherReference'] ||
		'proposersFeedback';

	const handleGoToQuestionsClick = useCallback(() => {
		navigateWithBatchId(VIEW_QUESTIONS_ANSWERS, {
			state: { answers, uiConfig, result, name, reviewer },
		});
	}, [answers, navigateWithBatchId, uiConfig, result, name, reviewer]);

	const handleOpenModal = useCallback(() => {
		if (!skipModal) {
			setIsModalVisible(true);
		} else {
			handleGoToQuestionsClick();
		}
	}, [handleGoToQuestionsClick, skipModal]);

	const handleCloseModal = useCallback(() => {
		setIsModalVisible(false);
	}, []);

	const ModalFooter = (
		<div
			className="flex font-sans justify-end gap-1 mb-1 items-center cursor-pointer"
			onClick={handleGoToQuestionsClick}
		>
			<Text className="font-bold text-sm text-primary-blue whitespace-nowrap">
				{t('goToQuestions')}
			</Text>
		</div>
	);

	return (
		<>
			<Tag
				className="h-6 mb-2 bg-light-green rounded-xl cursor-pointer"
				color={`${
					color === 'success'
						? 'rgba(18, 202, 116, 0.1)'
						: color === 'error'
						? 'rgba(254, 244, 231, 1)'
						: 'rgba(229, 235, 245, 1)'
				}`}
				onClick={handleOpenModal}
			>
				<div className="flex gap-2 items-center px-0.5 py-[1px]">
					<Text
						className={` font-sans font-normal text-sm ${
							color === 'success'
								? 'text-green'
								: color === 'error'
								? 'text-orange'
								: 'text-primary-blue'
						}`}
					>
						{t(reviewer)}
					</Text>
					<Icon
						src={
							color === 'success'
								? GreenRightArrow
								: color === 'error'
								? OrangeRightArrow
								: BlueRightArrow
						}
						width={8}
						height={4}
					/>
				</div>
			</Tag>
			<Modal
				title={
					<div className="w-full flex justify-center">
						<Text className="font-sans font-bold text-lg text-gray-650">
							{t(uiConfig?.title || name)}
						</Text>
					</div>
				}
				open={isModalVisible}
				onCancel={handleCloseModal}
				footer={!hideGoToQuestions ? ModalFooter : null}
				closeIcon={<Icon src={CloseIcon} width={8} height={4} />}
				className={
					isLengthyLanguage ? 'md:min-w-[600px]' : 'max-w-md sm:max-w-4xl'
				}
			>
				<div
					className={`h-9 flex items-center justify-center mt-6 text-center font-sans font-normal text-sm ${
						color === 'success'
							? 'bg-green-bg text-green'
							: color === 'error'
							? 'bg-orange-bg text-orange'
							: 'bg-btn-blue text-primary-blue'
					}`}
				>
					{t(reviewer)}
				</div>
				{predefinedReasons.length > 0 && (
					<div className="mt-6">
						<strong>Predefined Reasons:</strong>
						<ul className="mt-1">
							{predefinedReasons.map((reason, index) => (
								<li key={index}>{reason.reasonText}</li>
							))}
						</ul>
					</div>
				)}
				<div className="mt-5 mb-1 font-sans text-sm font-normal text-gray-650">
					{t(commentsLabel)}
				</div>
				<div className="p-4 min-h-24 font-sans font-normal text-sm text-gray-650 rounded-md border border-solid !border-gray-200">
					{comments}
				</div>
			</Modal>
		</>
	);
};

export const ReviewerResultTag = ({
	reviewer,
	result,
	answers,
	uiConfig,
	name,
	hideGoToQuestions = false,
}: {
	reviewer: string;
	result?: Feedback;
	answers?: Answers;
	uiConfig?: CategoryStepUIConfig;
	name: string;
	hideGoToQuestions?: boolean;
}) => {
	const lastQuestionComment = answers?.questionsAndAnswers?.length
		? answers?.questionsAndAnswers[answers?.questionsAndAnswers.length - 1]
				.additionalComment
		: undefined;

	switch (result?.review) {
		case ReviewAction.APPROVE:
			return (
				<ResultTag
					color="success"
					reviewer={reviewer}
					comments={result?.comments || lastQuestionComment}
					answers={answers}
					uiConfig={uiConfig}
					name={name}
					result={result}
					hideGoToQuestions={hideGoToQuestions}
				/>
			);

		case ReviewAction.APPROVE_IDK_CANDIDATE:
			return (
				<ResultTag
					color="success"
					reviewer={reviewer}
					comments={result?.comments || lastQuestionComment}
					answers={answers}
					uiConfig={uiConfig}
					name={name}
					result={result}
					hideGoToQuestions={hideGoToQuestions}
				/>
			);

		case ReviewAction.REJECT:
			return (
				<ResultTag
					color="error"
					reviewer={reviewer}
					comments={result?.comments || lastQuestionComment}
					predefinedReasons={result?.predefinedReasons}
					answers={answers}
					uiConfig={uiConfig}
					name={name}
					result={result}
					hideGoToQuestions={hideGoToQuestions}
				/>
			);

		case ReviewAction.PENDING:
			return (
				<ResultTag
					color="default"
					reviewer={reviewer}
					comments={result?.comments || lastQuestionComment}
					predefinedReasons={result?.predefinedReasons}
					answers={answers}
					uiConfig={uiConfig}
					name={name}
					result={result}
					hideGoToQuestions={hideGoToQuestions}
				/>
			);
	}

	return null;
};
